/**
 * FIFA World Cup 2026 — static reference data
 * Hosts: Canada · Mexico · United States
 * Format: 12 groups of 4 → Round of 32 → R16 → QF → SF → Final
 */

export const WORLD_CUP_API_LEAGUE_ID = 1;
export const WORLD_CUP_SEASON = 2026;

export const WORLD_CUP_START = "2026-06-11";
export const WORLD_CUP_END = "2026-07-19";

// Visibility window for nav/badges (1 Jun – 31 Jul 2026)
export const WORLD_CUP_VISIBLE_FROM = "2026-06-01";
export const WORLD_CUP_VISIBLE_TO = "2026-07-31";

export function isWorldCupWindow(now: Date = new Date()): boolean {
  const ts = now.getTime();
  return (
    ts >= new Date(WORLD_CUP_VISIBLE_FROM).getTime() &&
    ts <= new Date(`${WORLD_CUP_VISIBLE_TO}T23:59:59`).getTime()
  );
}

// Mata-mata phases (string returned by API-Football "round")
export const WORLD_CUP_KNOCKOUT_ROUNDS = [
  "Round of 32",
  "Round of 16",
  "Quarter-finals",
  "Semi-finals",
  "3rd Place Final",
  "Final",
] as const;

export type WorldCupRound = typeof WORLD_CUP_KNOCKOUT_ROUNDS[number];

// Official FIFA World Cup 2026 emblem (Wikimedia → uploaded to Lovable Assets CDN)
import wcEmblem from "@/assets/fifa-world-cup-2026-emblem.png.asset.json";
export const WORLD_CUP_OFFICIAL_LOGO = wcEmblem.url;
// Fallback (API-Football generic competition icon) if the primary fails to load
export const WORLD_CUP_FALLBACK_LOGO = "https://media.api-sports.io/football/leagues/1.png";


export const WORLD_CUP_ROUND_LABELS: Record<string, string> = {
  "Round of 32": "Round of 32",
  "Round of 16": "Round of 16",
  "Quarter-finals": "Quarter-finals",
  "Semi-finals": "Semi-finals",
  "3rd Place Final": "Third Place Play-off",
  "Final": "Final",
  "Group Stage - 1": "Group Stage · MD1",
  "Group Stage - 2": "Group Stage · MD2",
  "Group Stage - 3": "Group Stage · MD3",
};

export function labelRound(round?: string | null): string {
  if (!round) return "—";
  return WORLD_CUP_ROUND_LABELS[round] ?? round;
}


export interface WorldCupVenue {
  city: string;
  country: "USA" | "Canada" | "Mexico";
  name: string;
  capacity: number;
  timezone: string;
  /** Explicit image URL (Lovable Assets CDN) — wins over apiVenueId */
  imageUrl?: string;
  /** API-Football venue id — fallback when imageUrl is absent */
  apiVenueId?: number;
}

// Stadium photos (Wikimedia Commons → Lovable Assets CDN)
import stMercedes from "@/assets/stadiums/mercedes-benz.jpg.asset.json";
import stGillette from "@/assets/stadiums/gillette.jpg.asset.json";
import stAtt from "@/assets/stadiums/att.jpg.asset.json";
import stNrg from "@/assets/stadiums/nrg.jpg.asset.json";
import stArrowhead from "@/assets/stadiums/arrowhead.jpg.asset.json";
import stSofi from "@/assets/stadiums/sofi.jpg.asset.json";
import stHardRock from "@/assets/stadiums/hardrock.jpg.asset.json";
import stMetLife from "@/assets/stadiums/metlife.jpg.asset.json";
import stLincoln from "@/assets/stadiums/lincoln.jpg.asset.json";
import stLevis from "@/assets/stadiums/levis.jpg.asset.json";
import stLumen from "@/assets/stadiums/lumen.jpg.asset.json";
import stBbva from "@/assets/stadiums/bbva.jpg.asset.json";

/**
 * Official FIFA World Cup 2026 group assignments (final draw, Dec 2025),
 * keyed by team api_football_id. Used as the canonical source so group
 * labels are correct in the UI even before standings are populated.
 */
export const WORLD_CUP_TEAM_GROUPS: Record<number, string> = {
  1: "Group E",     // Belgium
  2: "Group K",     // France
  3: "Group I",     // Croatia
  5: "Group L",     // Sweden
  6: "Group F",     // Brazil
  7: "Group G",     // Uruguay
  8: "Group H",     // Colombia
  9: "Group G",     // Spain
  10: "Group I",    // England
  11: "Group I",    // Panama
  12: "Group L",    // Japan
  13: "Group K",    // Senegal
  15: "Group B",    // Switzerland
  16: "Group A",    // Mexico
  17: "Group A",    // South Korea
  20: "Group D",    // Australia
  22: "Group E",    // Iran
  23: "Group G",    // Saudi Arabia
  25: "Group J",    // Germany
  26: "Group C",    // Argentina
  27: "Group H",    // Portugal
  28: "Group L",    // Tunisia
  31: "Group F",    // Morocco
  32: "Group E",    // Egypt
  770: "Group A",   // Czech Republic
  775: "Group C",   // Austria
  777: "Group D",   // Türkiye
  1090: "Group K",  // Norway
  1108: "Group F",  // Scotland
  1113: "Group B",  // Bosnia & Herzegovina
  1118: "Group L",  // Netherlands
  1501: "Group J",  // Ivory Coast
  1504: "Group I",  // Ghana
  1508: "Group H",  // Congo DR
  1531: "Group A",  // South Africa
  1532: "Group C",  // Algeria
  1533: "Group G",  // Cape Verde Islands
  1548: "Group C",  // Jordan
  1567: "Group K",  // Iraq
  1568: "Group H",  // Uzbekistan
  1569: "Group B",  // Qatar
  2380: "Group D",  // Paraguay
  2382: "Group J",  // Ecuador
  2384: "Group D",  // USA
  2386: "Group F",  // Haiti
  4673: "Group E",  // New Zealand
  5529: "Group B",  // Canada
  5530: "Group J",  // Curaçao
};

// 16 stádios oficiais do Mundial 2026
export const WORLD_CUP_VENUES: WorldCupVenue[] = [
  // USA (11)
  { city: "Atlanta",        country: "USA",    name: "Mercedes-Benz Stadium",   capacity: 71000, timezone: "America/New_York",     imageUrl: stMercedes.url },
  { city: "Boston",         country: "USA",    name: "Gillette Stadium",        capacity: 65878, timezone: "America/New_York",     imageUrl: stGillette.url },
  { city: "Dallas",         country: "USA",    name: "AT&T Stadium",            capacity: 80000, timezone: "America/Chicago",      imageUrl: stAtt.url },
  { city: "Houston",        country: "USA",    name: "NRG Stadium",             capacity: 72220, timezone: "America/Chicago",      imageUrl: stNrg.url },
  { city: "Kansas City",    country: "USA",    name: "Arrowhead Stadium",       capacity: 76416, timezone: "America/Chicago",      imageUrl: stArrowhead.url },
  { city: "Los Angeles",    country: "USA",    name: "SoFi Stadium",            capacity: 70240, timezone: "America/Los_Angeles",  imageUrl: stSofi.url },
  { city: "Miami",          country: "USA",    name: "Hard Rock Stadium",       capacity: 65326, timezone: "America/New_York",     imageUrl: stHardRock.url },
  { city: "New York/NJ",    country: "USA",    name: "MetLife Stadium",         capacity: 82500, timezone: "America/New_York",     imageUrl: stMetLife.url },
  { city: "Philadelphia",   country: "USA",    name: "Lincoln Financial Field", capacity: 69796, timezone: "America/New_York",     imageUrl: stLincoln.url },
  { city: "San Francisco",  country: "USA",    name: "Levi's Stadium",          capacity: 68500, timezone: "America/Los_Angeles",  imageUrl: stLevis.url },
  { city: "Seattle",        country: "USA",    name: "Lumen Field",             capacity: 68740, timezone: "America/Los_Angeles",  imageUrl: stLumen.url },
  // Canada (2)
  { city: "Toronto",        country: "Canada", name: "BMO Field",               capacity: 45736, timezone: "America/Toronto",      apiVenueId: 312 },
  { city: "Vancouver",      country: "Canada", name: "BC Place",                capacity: 54500, timezone: "America/Vancouver",    apiVenueId: 19445 },
  // Mexico (3)
  { city: "Guadalajara",    country: "Mexico", name: "Estadio Akron",           capacity: 49850, timezone: "America/Mexico_City",  apiVenueId: 1076 },
  { city: "Mexico City",    country: "Mexico", name: "Estadio Azteca",          capacity: 87000, timezone: "America/Mexico_City",  apiVenueId: 1069 },
  { city: "Monterrey",      country: "Mexico", name: "Estadio BBVA",            capacity: 53500, timezone: "America/Monterrey",    imageUrl: stBbva.url },
];

// Aliases para nomes de estádios devolvidos pela API-Football (rebrand patrocinado)
export const VENUE_ALIASES: Record<string, string> = {
  "Estadio Banorte": "Estadio BBVA", // Monterrey rebrand
};

export const HOST_FLAG: Record<WorldCupVenue["country"], string> = {
  USA: "🇺🇸",
  Canada: "🇨🇦",
  Mexico: "🇲🇽",
};

// Fases canónicas para filtros de UI
export const WORLD_CUP_PHASES = [
  "Group Stage",
  "Round of 32",
  "Round of 16",
  "Quarter-finals",
  "Semi-finals",
  "Final / 3rd Place",
] as const;

export type WorldCupPhase = typeof WORLD_CUP_PHASES[number];

export const WORLD_CUP_PHASE_LABELS: Record<WorldCupPhase, string> = {
  "Group Stage": "Group Stage",
  "Round of 32": "Round of 32",
  "Round of 16": "Round of 16",
  "Quarter-finals": "Quarter-finals",
  "Semi-finals": "Semi-finals",
  "Final / 3rd Place": "Final / 3rd Place",
};


/**
 * Deriva a fase do Mundial 2026 a partir da data do jogo.
 * Enquanto a fase de grupos não estiver concluída, jogos cujos emparelhamentos
 * dependem dos grupos (28 Jun) são apresentados como "Group Stage" para não
 * anunciar pairings de R32 que ainda não existem oficialmente.
 *  11–28 Jun → Grupos · 29 Jun–3 Jul → R32 · 4–7 Jul → R16
 *  9–11 Jul → QF · 14–15 Jul → SF · 18–19 Jul → Final / 3º
 */
export function getWorldCupPhase(matchDate: string | Date): WorldCupPhase {
  const d = typeof matchDate === "string" ? new Date(matchDate) : matchDate;
  const month = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  if (month === 6 && day <= 28) return "Group Stage";
  if ((month === 6 && day >= 29) || (month === 7 && day <= 3)) return "Round of 32";
  if (month === 7 && day >= 4 && day <= 7) return "Round of 16";
  if (month === 7 && day >= 9 && day <= 11) return "Quarter-finals";
  if (month === 7 && day >= 14 && day <= 15) return "Semi-finals";
  if (month === 7 && (day === 18 || day === 19)) return "Final / 3rd Place";
  return "Group Stage";
}
